/**
 * Mobile account DTOs.
 *
 * Returned by `GET /api/mobile/v1/account` and accepted/returned by
 * `PATCH /api/mobile/v1/account`. The shape is wider than `SessionResponse`
 * — Account also exposes the editable preference + company display fields
 * and the `paymentFailedAt` timestamp the settings UI surfaces.
 *
 * Phase 09 shipped this as inline Zod inside the route (see route header
 * for why); Phase 09b promotes it here so mobile clients can import the
 * response shape from `@thesitebook/contracts` instead of inferring it
 * from the route.
 */
import { z } from "zod";
import { PlanStateSchema, PlanTierSchema } from "./plan-state";
import { UserRoleSchema } from "./session";

// ─── Read response ─────────────────────────────────────────────────────────

export const AccountUser = z
  .object({
    id: z
      .string()
      .describe(
        "The Site Book user id (mirrors Clerk user id 1:1; see SessionUser.id).",
      ),
    email: z.string().email(),
    role: UserRoleSchema,
    contactFirstName: z.string().nullable(),
    contactLastName: z.string().nullable(),
    contactPhone: z.string().nullable(),
  })
  .strict();

export type AccountUser = z.infer<typeof AccountUser>;

export const AccountCompany = z
  .object({
    name: z.string().nullable(),
    address: z.string().nullable(),
    email: z.string().nullable(),
    website: z.string().nullable(),
    logoUrl: z.string().nullable(),
  })
  .strict();

export type AccountCompany = z.infer<typeof AccountCompany>;

export const AccountPlan = z
  .object({
    tier: PlanTierSchema.describe("The resolved internal Account plan tier."),
    status: PlanStateSchema.describe(
      "Plan state derived from the resolved internal Account billing state.",
    ),
    trialEndsAt: z
      .string()
      .datetime()
      .nullable()
      .describe("Trial boundary from the resolved Account owner."),
    paymentFailedAt: z
      .string()
      .datetime()
      .nullable()
      .describe(
        "Timestamp of the resolved Account owner's most recent failed Stripe charge. Surfaced so the mobile settings UI can show a billing-action banner.",
      ),
  })
  .strict();

export type AccountPlan = z.infer<typeof AccountPlan>;

export const AccountPreferences = z
  .object({
    autoAiSuggest: z.boolean(),
    notifyEnabled: z.boolean(),
    notifyEmail: z.string().nullable(),
    notifyDaysBefore: z.number().int().min(1).max(365),
  })
  .strict();

export type AccountPreferences = z.infer<typeof AccountPreferences>;

export const AccountFlags = z
  .object({
    mobileWritesEnabled: z
      .boolean()
      .describe(
        "Mirrors the `mobile_writes_enabled` server-side kill switch. Same semantics as SessionFlags.mobileWritesEnabled.",
      ),
  })
  .strict();

export type AccountFlags = z.infer<typeof AccountFlags>;

export const AccountResponse = z
  .object({
    user: AccountUser,
    company: AccountCompany,
    plan: AccountPlan,
    preferences: AccountPreferences,
    flags: AccountFlags,
  })
  .strict();

export type AccountResponse = z.infer<typeof AccountResponse>;

// ─── PATCH request ─────────────────────────────────────────────────────────

/**
 * The mutable surface — only fields with a mobile UI control. Server
 * REJECTS anything outside this list (plan, email, stripe*, UTM/landing,
 * referral fields, etc.). Add a field here only when the mobile app
 * actually has a control for it.
 */
export const AccountPatchRequest = z
  .object({
    // contact
    contactFirstName: z.string().min(1).max(100).nullable().optional(),
    contactLastName: z.string().min(1).max(100).nullable().optional(),
    contactPhone: z.string().min(1).max(50).nullable().optional(),
    // company display fields
    companyName: z.string().min(1).max(200).nullable().optional(),
    companyAddress: z.string().min(1).max(500).nullable().optional(),
    companyEmail: z.string().email().max(255).nullable().optional(),
    companyWebsite: z.string().url().max(500).nullable().optional(),
    // preferences
    autoAiSuggest: z.boolean().optional(),
    notifyEnabled: z.boolean().optional(),
    notifyEmail: z.string().email().max(255).nullable().optional(),
    notifyDaysBefore: z.number().int().min(1).max(365).optional(),
  })
  .strict()
  .refine((d) => Object.keys(d).length > 0, {
    message: "Patch body must contain at least one mutable field",
  });

export type AccountPatchRequest = z.infer<typeof AccountPatchRequest>;
